import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Observable, finalize, of, switchMap } from 'rxjs';
import _ from 'lodash';
import { ApiResponse, BreadcrumbService } from '@card-software/library';
import { ClaimPolicyOption, policyOptionOf } from '../../../models/petty-cash-fund/petty-cash-fund';
import { ReceiptOwnerCode } from '../../../models/expense-receipt/expense-receipt';
import { ApprovalPolicyRoles, PettyCashRoles } from '../../../models/roles/roles-group';
import {
  ApprovalDecisionType,
  ApprovalSlot,
  ApproverType,
  CodedDocumentKey,
  Trip,
  TripStatus,
  TripUpdate,
} from '../../../models/trip/trip';
import { TripFormModel, tripFieldLabels } from '../../../models/trip/trip-form';
import { PettyCashFundProxyService } from '../../../proxies/petty-cash-fund.proxy.service';
import { TripProxyService } from '../../../proxies/trip.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';
import { CostCenterSelection } from '../../shared/cost-center-select/cost-center-select.component';
import { tripStatusLabel, tripStatusSeverity } from '../trip-status.util';

const LIST_ROUTE = '/trips';
const NEW_ID = 'new';

/** One approver seat as the approval panel shows it. */
interface SlotView {
  slot: ApprovalSlot;
  state: 'approved' | 'rejected' | 'pending';
  by: string | null;
  at: string | null;
  comment: string | null;
}

/** The lifecycle strip at the top of the page. */
interface StepView {
  status: TripStatus;
  label: string;
  done: boolean;
  current: boolean;
}

/**
 * One employee trip (#5790): a TripAdmin creates it for a Standard employee and submits it; the Trip default
 * policy's approvers (frozen on submit) approve or reject it. A Rejected trip can be edited and submitted again.
 * Nothing posts here — advances (#5789) and claims (#5795) move the money.
 */
@Component({
  selector: 'app-trip-details',
  templateUrl: './trip-details.component.html',
})
export class TripDetailsComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(TripProxyService);
  private readonly fundProxy = inject(PettyCashFundProxyService);
  private readonly userAuth = inject(UserAuthenticationService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly canEdit = this.userAuth.roles.includes(PettyCashRoles.TripAdmin);
  /**
   * UserMaster's policy list needs ApprovalPolicyViewer/Admin, so only someone who can change the trip's policy and
   * holds one loads it; everyone else (approvers, viewers) sees the trip's own policy and gets no permission warning.
   */
  private readonly canListPolicies =
    this.canEdit &&
    [ApprovalPolicyRoles.ApprovalPolicyViewer, ApprovalPolicyRoles.ApprovalPolicyAdmin].some((role) => this.userAuth.roles.includes(role));
  readonly fieldLabels = tripFieldLabels;

  readonly isLoading = signal(0);
  readonly isNew = signal(true);
  readonly currentId = signal<string | null>(null);
  readonly trip = signal<Trip | null>(null);
  readonly ready = signal(false);
  readonly policies = signal<ClaimPolicyOption[]>([]);
  readonly employeeSuggestions = signal<CodedDocumentKey[]>([]);

  /** The approve / reject dialog. */
  readonly decisionVisible = signal(false);
  readonly decisionType = signal<ApprovalDecisionType>(ApprovalDecisionType.Approved);
  readonly decisionComment = new FormControl<string>('', { nonNullable: true });

  readonly headerTitle = computed(() => (this.isNew() ? this.label('Trip_New_Title', 'New trip') : (this.trip()?.number ?? '')));

  readonly saveMenuItems: MenuItem[] = [
    { label: this.label('Save and Close', 'Save and Close'), icon: 'pi pi-save', command: () => this.save(true) },
  ];

  readonly form = new FormGroup<ControlsOf<TripFormModel>>({
    employee: new FormControl<CodedDocumentKey | null>(null, Validators.required),
    destination: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    departureDate: new FormControl<Date | null>(null, Validators.required),
    returnDate: new FormControl<Date | null>(null, Validators.required),
    purpose: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    costCenterReference: new FormControl<string | null>(null, Validators.required),
    claimPolicyReference: new FormControl<string | null>(null),
  });

  readonly status = computed(() => this.trip()?.status ?? TripStatus.Draft);
  readonly isEditable = computed(() => this.canEdit && (this.isNew() || [TripStatus.Draft, TripStatus.Rejected].includes(this.status())));
  readonly canSubmit = computed(() => this.isEditable());
  readonly canDelete = computed(() => this.canEdit && !this.isNew() && this.status() === TripStatus.Draft);
  readonly canDecide = computed(() => this.trip()?.canDecide === true);
  readonly isOpen = computed(() => this.status() === TripStatus.Open);
  /** PettyCashTreasurer pays advances (#5789) and settles the trip's claims (#5799). */
  readonly canTreasury = this.userAuth.roles.includes(PettyCashRoles.PettyCashTreasurer);
  readonly canSettle = computed(() => this.canTreasury && [TripStatus.Open, TripStatus.InSettlement].includes(this.status()));
  readonly tripStatuses = TripStatus;
  /** The trip's employee or a TripAdmin prepares its expense claims (#5795). */
  readonly canClaim = computed(
    () => this.canEdit || (this.trip()?.employeeUsername ?? '').toLowerCase() === (this.userAuth.userLite?.username ?? '-').toLowerCase(),
  );
  readonly hasExtraButtons = computed(() => this.canDelete() || this.canSubmit() || this.canDecide() || this.isOpen());
  readonly isRejection = computed(() => this.decisionType() === ApprovalDecisionType.Rejected);

  readonly costCenterCurrent = computed<CostCenterSelection | null>(() => {
    const current = this.trip()?.costCenter;
    return _.isNil(current) ? null : { code: current.code, name: current.name };
  });

  readonly steps = computed<StepView[]>(() => {
    const status = this.status();
    const order = [TripStatus.Draft, TripStatus.Submitted, TripStatus.Open, TripStatus.InSettlement, TripStatus.Closed];
    // A rejected trip sits where it was decided: past Draft, at Submitted.
    const index = order.indexOf(status === TripStatus.Rejected ? TripStatus.Submitted : status);
    return order.map((step, i) => ({
      status: step,
      label: this.statusLabel(step),
      done: i < index,
      current: i === index,
    }));
  });

  readonly slotViews = computed<SlotView[]>(() => {
    const approval = this.trip()?.approval;
    if (_.isNil(approval)) return [];
    return _.sortBy(approval.slots, (s) => s.order ?? Number.MAX_SAFE_INTEGER).map((slot) => {
      const decision = approval.decisions.find((d) => d.slotId === slot.id) ?? null;
      return {
        slot,
        state: _.isNil(decision) ? 'pending' : decision.decision === ApprovalDecisionType.Approved ? 'approved' : 'rejected',
        by: decision?.username ?? null,
        at: decision?.at ?? null,
        comment: decision?.comment ?? null,
      };
    });
  });

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (_.isNil(id)) {
      this.back();
      return;
    }
    this.isNew.set(id === NEW_ID);
    this.currentId.set(id === NEW_ID ? null : id);
    if (this.canListPolicies) this.loadPolicies();
    if (this.isNew()) {
      this.breadcrumbService.setCurrent('Common_New');
      this.applyEditability();
      this.ready.set(true);
    } else {
      this.loadItem(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadPolicies(): void {
    this.fundProxy.getClaimPolicies().subscribe((response) => this.policies.set(response.data?.items ?? []));
  }

  private loadItem(id: string): void {
    this.startLoading();
    this.proxy
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const trip = response.data;
        if (!_.isNil(trip)) this.setTrip(trip);
      });
  }

  private setTrip(trip: Trip): void {
    this.trip.set(trip);
    this.breadcrumbService.setCurrent(trip.number);
    if (!this.canListPolicies) this.policies.set(policyOptionOf(trip.claimPolicy));
    this.form.patchValue(
      {
        employee: trip.employee,
        destination: trip.destination,
        departureDate: this.fromApiDate(trip.departureDate),
        returnDate: this.fromApiDate(trip.returnDate),
        purpose: trip.purpose,
        costCenterReference: trip.costCenter.reference,
        claimPolicyReference: trip.claimPolicy?.reference ?? null,
      },
      { emitEvent: false },
    );
    this.applyEditability();
    this.form.markAsPristine();
    this.ready.set(true);
  }

  private applyEditability(): void {
    if (this.isEditable()) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
  }

  searchEmployees(event: AutoCompleteCompleteEvent): void {
    this.proxy.getEmployees(event.query ?? '').subscribe((response) => this.employeeSuggestions.set(response.data ?? []));
  }

  save(shouldClose = false): void {
    this.persist().subscribe((saved) => {
      if (_.isNil(saved)) return;
      GlobalMessageUtil.success(this.label('Trip_Title', 'Trips'), this.label('Trip_Saved', 'Trip saved.'));
      if (shouldClose) {
        this.back();
        return;
      }
      this.afterSave(saved);
    });
  }

  /** Saves pending edits first, then sends the trip to its approvers. */
  submit(): void {
    const pending = this.isNew() || this.form.dirty ? this.persist() : of(this.trip());
    pending
      .pipe(
        switchMap((saved) => {
          if (_.isNil(saved)) return of(null);
          this.afterSave(saved);
          this.startLoading();
          return this.proxy.submit(saved.id).pipe(finalize(() => this.stopLoading()));
        }),
      )
      .subscribe((response) => {
        const submitted = response?.data;
        if (_.isNil(submitted)) return;
        GlobalMessageUtil.success(
          this.label('Trip_Title', 'Trips'),
          submitted.status === TripStatus.Open
            ? this.label('Trip_Submitted_Open', 'No approval needed: the trip is open.')
            : this.label('Trip_Submitted', 'Trip sent for approval.'),
        );
        this.setTrip(submitted);
      });
  }

  /** Validates and saves the form; emits the saved trip, or null when the form is invalid. */
  private persist(): Observable<Trip | null> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return of(null);
    }
    const value = this.form.getRawValue();
    if (_.isNil(value.employee) || _.isNil(value.departureDate) || _.isNil(value.returnDate) || _.isNil(value.costCenterReference)) {
      return of(null);
    }
    const payload: TripUpdate = {
      employeeReference: value.employee.reference,
      destination: value.destination.trim(),
      departureDate: this.toApiDate(value.departureDate),
      returnDate: this.toApiDate(value.returnDate),
      purpose: value.purpose.trim(),
      costCenterReference: value.costCenterReference,
      claimPolicyReference: value.claimPolicyReference,
    };
    const id = this.currentId();
    const request: Observable<ApiResponse<Trip>> = _.isNil(id) ? this.proxy.create(payload) : this.proxy.update(id, payload);
    this.startLoading();
    return request.pipe(
      finalize(() => this.stopLoading()),
      switchMap((response) => of(response.data ?? null)),
    );
  }

  private afterSave(saved: Trip): void {
    if (this.isNew()) {
      this.isNew.set(false);
      this.currentId.set(saved.id);
      this.router.navigate([], { queryParams: { id: saved.id }, replaceUrl: true });
    }
    this.setTrip(saved);
  }

  openDecision(type: ApprovalDecisionType): void {
    this.decisionType.set(type);
    this.decisionComment.reset('');
    this.decisionVisible.set(true);
  }

  openApprove(): void {
    this.openDecision(ApprovalDecisionType.Approved);
  }

  openReject(): void {
    this.openDecision(ApprovalDecisionType.Rejected);
  }

  confirmDecision(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    const comment = this.decisionComment.value.trim();
    if (this.isRejection() && comment === '') {
      this.decisionComment.markAsTouched();
      return;
    }
    const request = this.isRejection() ? this.proxy.reject(id, comment) : this.proxy.approve(id, comment || null);
    this.startLoading();
    request.pipe(finalize(() => this.stopLoading())).subscribe((response) => {
      const trip = response.data;
      if (_.isNil(trip)) return;
      this.decisionVisible.set(false);
      GlobalMessageUtil.success(
        this.label('Trip_Title', 'Trips'),
        this.isRejection() ? this.label('Trip_Rejected', 'Trip rejected.') : this.label('Trip_Approved', 'Approval recorded.'),
      );
      this.setTrip(trip);
    });
  }

  confirmDelete(): void {
    const trip = this.trip();
    if (_.isNil(trip)) return;
    this.confirmationService.confirm({
      header: this.label('Trip_Delete_Title', 'Delete trip'),
      message: this.label('Trip_Delete_Message', 'Delete this draft trip? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(trip.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            GlobalMessageUtil.success(this.label('Trip_Title', 'Trips'), this.label('Trip_Deleted', 'Trip deleted.'));
            this.back();
          });
      },
    });
  }

  /** Receipt capture for this trip (#5794); the API lets the trip's employee or a TripAdmin in. */
  captureReceipts(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.router.navigate(['/expense-receipts'], { queryParams: { ownerReference: id, ownerCode: ReceiptOwnerCode.Trip } });
  }

  /** A new expense claim for this trip (#5800). */
  newClaim(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.router.navigate(['/expense-claim-details'], { queryParams: { id: 'new', ownerReference: id, ownerCode: ReceiptOwnerCode.Trip } });
  }

  /** A new advance for this trip (#5789). */
  newAdvance(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.router.navigate(['/employee-advance-details'], { queryParams: { id: 'new', tripReference: id } });
  }

  /** A new settlement of this trip's posted claims, netting its advances (#5799). */
  settle(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.router.navigate(['/expense-settlement-details'], {
      queryParams: { id: 'new', ownerReference: id, ownerCode: ReceiptOwnerCode.Trip },
    });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  statusLabel(status: TripStatus): string {
    return tripStatusLabel(status, (k, f) => this.label(k, f));
  }

  statusSeverity(status: TripStatus): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    return tripStatusSeverity(status);
  }

  policyName(policy: { name: { en: string; es: string } }): string {
    return this.translate.currentLang === 'es' ? policy.name.es : policy.name.en;
  }

  slotIcon(slot: ApprovalSlot): string {
    return slot.type === ApproverType.Role ? 'pi pi-users' : 'pi pi-user';
  }

  /** yyyy-MM-dd of the picked local day, so the server stores that calendar day. */
  private toApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** The stored calendar day as a local Date (the server keeps midnight UTC). */
  private fromApiDate(value: string | null): Date | null {
    if (_.isNil(value)) return null;
    const [year, month, day] = value.substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
